import * as React from 'react';
import { FlashList, FlashListProps, FlashListRef } from '@shopify/flash-list';
import { useSizing } from 'hooks/useSizing';
import { theme } from 'lib/theme';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator } from './BlockingLoader/ActivityIndicator';
import { PageEmpty, PageEmptyProps } from './PageEmpty';

export type ListProps<ItemT> = {
  data: FlashListProps<ItemT>['data'];
  threshold?: number;
  onEndReached?: () => void;
  renderItem: FlashListProps<ItemT>['renderItem'];
  getItemType?: FlashListProps<ItemT>['getItemType'];
  extraData?: FlashListProps<ItemT>['extraData'];
  ListFooterComponent?: FlashListProps<ItemT>['ListFooterComponent'];
  ListHeaderComponent?: FlashListProps<ItemT>['ListHeaderComponent'];
  ListHeaderComponentStyle?: FlashListProps<ItemT>['ListHeaderComponentStyle'];
  numberOfColumns?: number;
  scrollIndicatorInsets?: FlashListProps<ItemT>['scrollIndicatorInsets'];
  keyExtractor: FlashListProps<ItemT>['keyExtractor'];
  contentContainerStyle?: Omit<
    FlashListProps<ItemT>['contentContainerStyle'],
    'padding' | 'paddingLeft' | 'paddingRight' | 'paddingBottom' | 'paddingTop'
  >;
  ItemSeparatorComponent?: FlashListProps<ItemT>['ItemSeparatorComponent'];
  stickyHeaderIndices?: FlashListProps<ItemT>['stickyHeaderIndices'];
  stickyHeaderConfig?: FlashListProps<ItemT>['stickyHeaderConfig'];
  onChangeStickyIndex?: FlashListProps<ItemT>['onChangeStickyIndex'];
  onScroll?: FlashListProps<ItemT>['onScroll'];
  onLayout?: FlashListProps<ItemT>['onLayout'];
  refreshControl?: FlashListProps<ItemT>['refreshControl'];
  maintainVisibleContentPosition?: FlashListProps<ItemT>['maintainVisibleContentPosition'];
  listRef?: React.ForwardedRef<ListRef<ItemT>> | null;
  paddingTop?: number;
  paddingLeft?: number;
  paddingRight?: number;
  paddingBottom?: number;
  isLoading?: boolean;
  emptyConfig?: PageEmptyProps;
};

export type ListRef<T> = {
  scrollToItem: FlashListRef<T>['scrollToItem'];
};

export const List = <T,>({
  contentContainerStyle,
  data,
  ItemSeparatorComponent,
  keyExtractor,
  ListFooterComponent,
  ListHeaderComponent,
  ListHeaderComponentStyle,
  numberOfColumns = 1,
  onEndReached,
  renderItem,
  scrollIndicatorInsets,
  threshold = 0.9,
  extraData,
  stickyHeaderIndices,
  stickyHeaderConfig,
  onChangeStickyIndex,
  onScroll,
  refreshControl,
  getItemType,
  maintainVisibleContentPosition,
  listRef,
  onLayout,
  paddingTop = theme.padding * 2,
  paddingLeft = 0,
  paddingRight = 0,
  paddingBottom = theme.padding * 2,
  emptyConfig,
  isLoading,
}: ListProps<T>): // ref: React.ForwardedRef<ListRef<T>>,
React.ReactElement => {
  const sizing = useSizing();
  const flastListRef = React.useRef<FlashListRef<T>>(null);
  const resolvedStickyHeaderConfig = stickyHeaderIndices?.length
    ? {
        offset: sizing.headerHeight,
        ...stickyHeaderConfig,
      }
    : stickyHeaderConfig;
  const stickyHeaderOffset = resolvedStickyHeaderConfig?.offset ?? 0;

  React.useImperativeHandle(
    listRef,
    () => ({
      scrollToItem: (...args) => flastListRef.current!.scrollToItem(...args),
    }),
    [flastListRef],
  );

  return (
    <View style={{ position: 'relative', flex: 1 }}>
      {isLoading && (
        <View
          accessibilityRole="progressbar"
          accessibilityLabel="Please Wait"
          style={[
            StyleSheet.absoluteFill,
            {
              alignItems: 'center',
              backgroundColor: theme.colors.white50,
              justifyContent: 'center',
              zIndex: 2,
            },
          ]}
        >
          <ActivityIndicator size={60} />
        </View>
      )}
      <FlashList
        ref={flastListRef}
        refreshControl={refreshControl}
        onScroll={onScroll}
        style={{
          flex: 1,
        }}
        contentContainerStyle={[
          {
            paddingTop: sizing.headerHeight + paddingTop - stickyHeaderOffset,
            paddingBottom: sizing.safeAreaBottom + paddingBottom,
            // paddingLeft: sizing.safeAreaLeft + paddingLeft,
            // paddingRight: sizing.safeAreaRight + paddingRight,
            paddingLeft: paddingLeft,
            paddingRight: paddingRight,
          },
          contentContainerStyle,
        ]}
        scrollIndicatorInsets={
          scrollIndicatorInsets ?? {
            top: sizing.headerHeight,
          }
        }
        key={numberOfColumns}
        numColumns={numberOfColumns}
        keyExtractor={keyExtractor}
        ListEmptyComponent={emptyConfig ? <PageEmpty {...emptyConfig} /> : null}
        data={data}
        extraData={extraData}
        renderItem={renderItem}
        onEndReachedThreshold={threshold}
        onEndReached={onEndReached}
        ListHeaderComponentStyle={ListHeaderComponentStyle}
        ListHeaderComponent={ListHeaderComponent}
        ListFooterComponent={ListFooterComponent}
        ItemSeparatorComponent={ItemSeparatorComponent}
        stickyHeaderIndices={stickyHeaderIndices}
        stickyHeaderConfig={resolvedStickyHeaderConfig}
        onChangeStickyIndex={onChangeStickyIndex}
        getItemType={getItemType}
        showsVerticalScrollIndicator
        maintainVisibleContentPosition={maintainVisibleContentPosition}
        onLayout={onLayout}
      />
    </View>
  );
};
